import { API_CONFIG } from '../config/api';

interface PublicSettings {
  company_name: string;
  company_logo: string;
}

interface PublicSettingsResponse {
  settings: PublicSettings;
}

const CACHE_KEY = 'public_settings';
const CACHE_TTL = 300000; // 5 minutes

interface CachedSettings {
  data: PublicSettings;
  timestamp: number;
}

// Get cached settings if available and not expired
function getCachedSettings(): PublicSettings | null {
  try {
    // Check if localStorage is available (might not be in incognito mode)
    if (typeof localStorage === 'undefined') return null;
    
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedSettings = JSON.parse(cached);
    const now = Date.now();
    
    if (now - parsed.timestamp > CACHE_TTL) {
      // Cache expired
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    return parsed.data;
  } catch (error) {
    // Invalid cache data or localStorage unavailable
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // Ignore if localStorage is unavailable
    }
    return null;
  }
}

// Cache settings
function cacheSettings(settings: PublicSettings): void {
  try {
    // Check if localStorage is available
    if (typeof localStorage === 'undefined') return;
    
    const cacheData: CachedSettings = {
      data: settings,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    // Ignore cache errors (e.g., in incognito mode)
    console.warn('Failed to cache public settings:', error);
  }
}

// Default settings as fallback
const DEFAULT_SETTINGS: PublicSettings = {
  company_name: 'EV Charging',
  company_logo: '',
};

export async function fetchPublicSettings(): Promise<PublicSettings> {
  // Check cache first
  const cached = getCachedSettings();
  if (cached) {
    return cached;
  }
  
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/api/public/settings`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      if (response.status === 429) {
        console.warn('Rate limited when fetching public settings');
      }
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data: PublicSettingsResponse = await response.json();
    const settings = data.settings || DEFAULT_SETTINGS;
    
    // Cache the successful response
    cacheSettings(settings);
    
    return settings;
  } catch (error) {
    console.error('Failed to fetch public settings:', error);
    // Return defaults on error
    return DEFAULT_SETTINGS;
  }
}

// Clear cache when needed (e.g., after settings update)
export function clearPublicSettingsCache(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch (error) {
    // Ignore localStorage errors
    console.warn('Failed to clear public settings cache:', error);
  }
  
  // Dispatch a custom event to force refresh all components
  window.dispatchEvent(new CustomEvent('publicSettingsUpdated'));
}